/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import {
    GYSectionCard, GYSectionHeader, GYSectionBody,
    GYTimelineContainer, GYTimelineItem, GYTimelineCard,
    GYTimelineHeader, GYTimelineDate, GYTimelineDiag,
    GYTimelineBody, GYTimelineField, GYTimelineFieldLabel, GYTimelineFieldValue,
    GYTimelineActions, GYTimelineBtn,
    GYEmptyState, GYEmptyIcon, GYEmptyTitle, GYEmptyDesc,
    GYStatusBadge, GYTagPill,
} from "./GynaeConsultationStyles";

// import {} from "./hooks/useGynaeConsultation";

/* ═══════════════════════════════════════════════════════════════
   GynaeHistoryTimeline — Consultation History Timeline (Task 18)
   Shows past gynaecological consultations chronologically with
   expandable details and edit capability.
   ═══════════════════════════════════════════════════════════════ */

const getComplaintLabel = function (value) {
    if (!value) return "None specified";
    return String(value);
};

const formatDate = function (dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        const options = { year: "numeric", month: "short", day: "numeric" };
        return d.toLocaleDateString("en-GB", options);
    } catch (e) {
        return dateStr;
    }
};

const formatDateTime = function (dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        const options = { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
        return d.toLocaleDateString("en-GB", options);
    } catch (e) {
        return dateStr;
    }
};

export const GynaeHistoryTimeline = function (props) {
    const gyn = props.gyn;

    /* ─── Track which cards are expanded ─── */
    const expandState = useState({});
    const expanded = expandState[0];
    const setExpanded = expandState[1];

    const toggleExpand = function (id) {
        setExpanded(function (prev) {
            const next = Object.assign({}, prev);
            next[id] = !next[id];
            return next;
        });
    };

    /* ─── Sort records by date descending ─── */
    const sortedRecords = gyn.consultations.slice().sort(function (a, b) {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    /* ─── Empty state ─── */
    if (sortedRecords.length === 0) {
        return (
            <GYSectionCard>
                <GYSectionHeader color="#2c3e50">
                    Consultation History
                </GYSectionHeader>
                <GYSectionBody>
                    <GYEmptyState>
                        <GYEmptyIcon>!</GYEmptyIcon>
                        <GYEmptyTitle>No Previous Consultations</GYEmptyTitle>
                        <GYEmptyDesc>
                            This patient has no prior gynaecological consultation records.
                            Use the "Consultation" tab to record a new consultation.
                        </GYEmptyDesc>
                    </GYEmptyState>
                </GYSectionBody>
            </GYSectionCard>
        );
    }

    return (
        <div>
            <GYSectionCard>
                <GYSectionHeader color="#2c3e50">
                    Consultation History ({sortedRecords.length} record{sortedRecords.length !== 1 ? "s" : ""})
                </GYSectionHeader>
                <GYSectionBody>
                    <GYTimelineContainer>
                        {sortedRecords.map(function (record) {
                            const isOpen = expanded[record.id] || false;
                            const mh = record.menstrual_history || {};
                            const exam = record.examination_findings || {};

                            return (
                                <GYTimelineItem key={record.id}>
                                    <GYTimelineCard>
                                        <GYTimelineHeader
                                            onClick={function () { toggleExpand(record.id); }}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                                                <GYTimelineDate>
                                                    {formatDateTime(record.created_at)}
                                                </GYTimelineDate>
                                                <GYStatusBadge color="#005a8b">
                                                    {getComplaintLabel(record.presenting_complaint).length > 30 ? getComplaintLabel(record.presenting_complaint).substring(0, 30) + '...' : getComplaintLabel(record.presenting_complaint)}
                                                </GYStatusBadge>
                                            </div>
                                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                                <span style={{ fontSize: "0.75rem", color: "#888" }}>
                                                    {isOpen ? "Collapse" : "Expand"}
                                                </span>
                                            </div>
                                        </GYTimelineHeader>

                                        <GYTimelineBody open={isOpen}>
                                            {isOpen && (
                                                <div>
                                                    {/* ─── Complaint ─── */}
                                                    <div style={{ marginBottom: "12px" }}>
                                                        <strong style={{ fontSize: "0.82rem", color: "#005a8b" }}>Presenting Complaint</strong>
                                                    </div>
                                                    <GYTimelineField>
                                                        <GYTimelineFieldLabel>Complaint:</GYTimelineFieldLabel>
                                                        <GYTimelineFieldValue>{getComplaintLabel(record.presenting_complaint)}</GYTimelineFieldValue>
                                                    </GYTimelineField>

                                                    {/* ─── Menstrual History ─── */}
                                                    {Object.keys(mh).length > 0 && (
                                                        <div style={{ marginTop: "12px" }}>
                                                            <div style={{ marginBottom: "8px" }}>
                                                                <strong style={{ fontSize: "0.82rem", color: "#005a8b" }}>Menstrual History</strong>
                                                            </div>
                                                            <GYTimelineField>
                                                                <GYTimelineFieldLabel>LMP:</GYTimelineFieldLabel>
                                                                <GYTimelineFieldValue>{formatDate(mh.lmp)}</GYTimelineFieldValue>
                                                            </GYTimelineField>
                                                            <GYTimelineField>
                                                                <GYTimelineFieldLabel>Cycle:</GYTimelineFieldLabel>
                                                                <GYTimelineFieldValue>{mh.cycle_length || "—"} day cycle, {mh.period_duration || "—"} day duration, {mh.regularity || "—"}</GYTimelineFieldValue>
                                                            </GYTimelineField>
                                                            <GYTimelineField>
                                                                <GYTimelineFieldLabel>Flow:</GYTimelineFieldLabel>
                                                                <GYTimelineFieldValue>{mh.flow_amount || "—"} | Dysmenorrhea: {mh.dysmenorrhea || "—"}</GYTimelineFieldValue>
                                                            </GYTimelineField>
                                                        </div>
                                                    )}

                                                    {/* ─── Obstetric Summary ─── */}
                                                    {record.obstetric_summary && (
                                                        <GYTimelineField style={{ marginTop: "8px" }}>
                                                            <GYTimelineFieldLabel>Obstetric Summary:</GYTimelineFieldLabel>
                                                            <GYTimelineFieldValue>
                                                                <GYStatusBadge color="#4a148c">GPAL: {record.obstetric_summary}</GYStatusBadge>
                                                            </GYTimelineFieldValue>
                                                        </GYTimelineField>
                                                    )}

                                                    {/* ─── Key Exam Findings ─── */}
                                                    {Object.keys(exam).length > 0 && (
                                                        <div style={{ marginTop: "12px" }}>
                                                            <div style={{ marginBottom: "8px" }}>
                                                                <strong style={{ fontSize: "0.82rem", color: "#005a8b" }}>Examination Findings</strong>
                                                            </div>
                                                            {Object.keys(exam).map(function (secKey) {
                                                                const secData = exam[secKey] || {};
                                                                const entries = Object.entries(secData).filter(function (e) { return e[1] && e[1] !== ""; });
                                                                if (entries.length === 0) return null;
                                                                const sectionLabel = secKey.replace(/_/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
                                                                return (
                                                                    <div key={secKey} style={{ marginBottom: "6px" }}>
                                                                        <GYTimelineField>
                                                                            <GYTimelineFieldLabel>{sectionLabel}:</GYTimelineFieldLabel>
                                                                            <GYTimelineFieldValue>
                                                                                {entries.map(function (entry) {
                                                                                    const fieldLabel = entry[0].replace(/_/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
                                                                                    return fieldLabel + ": " + entry[1];
                                                                                }).join(" | ")}
                                                                            </GYTimelineFieldValue>
                                                                        </GYTimelineField>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {/* ─── Seen By ─── */}
                                                    {record.seen_by && (
                                                        <GYTimelineField style={{ marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #eee" }}>
                                                            <GYTimelineFieldLabel>Seen By:</GYTimelineFieldLabel>
                                                            <GYTimelineFieldValue>{record.seen_by}</GYTimelineFieldValue>
                                                        </GYTimelineField>
                                                    )}
                                                </div>
                                            )}
                                        </GYTimelineBody>

                                        <GYTimelineActions>
                                            <GYTimelineBtn
                                                color="#005a8b"
                                                onClick={function () { toggleExpand(record.id); }}
                                            >
                                                {isOpen ? "Collapse" : "Expand Details"}
                                            </GYTimelineBtn>
                                            <GYTimelineBtn
                                                color="#0d47a1"
                                                onClick={function () { gyn.startEditRecord(record); }}
                                            >
                                                Edit
                                            </GYTimelineBtn>
                                        </GYTimelineActions>
                                    </GYTimelineCard>
                                </GYTimelineItem>
                            );
                        })}
                    </GYTimelineContainer>
                </GYSectionBody>
            </GYSectionCard>
        </div>
    );
};
